import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import { PageHeader, StatCard, SectionCard, StatusBadge, Stars, Empty } from "../components/Shared";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Eye, FileText, FileCheck, Star, AlertTriangle, Inbox } from "lucide-react";

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard/vendor").then((r) => setData(r.data)).catch(() => setData({ completeness: 0, profile_views: 0, rfqs_received: 0, active_contracts: 0, average_rating: 0, recent_rfqs: [], expiring_documents: [], unanswered_rfqs: [] })); }, []);

  if (!data) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Vendor Dashboard" subtitle="Track your performance and pipeline" />

      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold">Profile completeness</div>
            <div className="text-xs text-zinc-500 mt-0.5">A complete profile attracts 3× more RFQs</div>
          </div>
          <div className="text-2xl font-bold font-outfit text-[#6366F1]">{data.completeness || 0}%</div>
        </div>
        <Progress value={data.completeness || 0} className="h-2" />
        {data.completeness < 100 && (
          <Button size="sm" className="mt-3 bg-[#6366F1] hover:bg-[#4F46E5]" onClick={() => navigate("/profile/edit")} data-testid="complete-profile-btn">Complete your profile →</Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Profile Views" value={data.profile_views || 0} hint="This month" icon={Eye} testid="stat-views" />
        <StatCard label="RFQs Received" value={data.rfqs_received || 0} hint="All time" icon={FileText} accent="#3B82F6" />
        <StatCard label="Active Contracts" value={data.active_contracts || 0} hint="Closed RFQs" icon={FileCheck} accent="#10B981" />
        <StatCard label="Average Rating" value={data.average_rating || 0} hint={<Stars value={data.average_rating} />} icon={Star} accent="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Recent RFQs" action={<Button size="sm" variant="ghost" onClick={() => navigate("/rfq/inbox")}>View inbox →</Button>}>
          {(data.recent_rfqs || []).length === 0 && <Empty title="No RFQs yet" subtitle="When buyers send you RFQs, they'll appear here" />}
          <div className="space-y-2">
            {(data.recent_rfqs || []).map((r) => (
              <button key={r.id} onClick={() => navigate("/rfq/inbox")} className="w-full text-left flex items-center justify-between p-3 hover:bg-zinc-50 rounded-md border border-zinc-100" data-testid="vendor-rfq-item">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{r.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{r.buyer_name} · Deadline {formatDate(r.deadline)}</div>
                </div>
                <StatusBadge status={r.status} />
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Action items">
          <div className="space-y-3">
            {(data.expiring_documents || []).map((d) => (
              <div key={d.id} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium">{d.name} expiring soon</div>
                  <div className="text-xs text-zinc-600">Expires in {d.days_to_expiry} days — {formatDate(d.expiry_date)}</div>
                </div>
              </div>
            ))}
            {(data.unanswered_rfqs || []).map((r) => (
              <div key={r.id} className="flex items-start gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
                <Inbox className="w-4 h-4 text-[#6366F1] flex-shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <div className="font-medium">Unanswered RFQ: {r.title}</div>
                  <div className="text-xs text-zinc-600">{r.buyer_name} · Deadline {formatDate(r.deadline)}</div>
                </div>
              </div>
            ))}
            {(data.expiring_documents || []).length === 0 && (data.unanswered_rfqs || []).length === 0 && (
              <Empty title="You're all caught up" subtitle="No pending actions" />
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
